import React from 'react';
import {
  X,
  Bell,
  Download,
  FileSpreadsheet,
  History,
  Users,
  MapPin,
  PackagePlus,
  Loader2,
  CheckCircle2,
  AlertCircle,
  CircleSlash,
  TriangleAlert,
  Clock,
  CircleHelp,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { ProgressBar } from './ProgressBar';

/**
 * The Centro de Actividad: what the backend has been doing on the user's behalf.
 *
 * Presentational on purpose — everything arrives through props. This lives in
 * `components/ui/`, which is not allowed to import from `features/`, so the
 * `useSelector` happens one level up in `layout/Header.jsx`.
 */

// Kept here rather than in `notificationsConstants` so the feature slice stays
// free of UI imports. The keys are the channel prefixes.
const KIND_ICONS = {
  download: FileSpreadsheet,
  download_history: History,
  download_sellers: Users,
  shipping_zone_coverage: MapPin,
  order_upload: PackagePlus,
};

const STATUS_STYLES = {
  running: { icon: Loader2, className: 'text-info animate-spin' },
  success: { icon: CheckCircle2, className: 'text-ninja-green' },
  error: { icon: AlertCircle, className: 'text-danger' },
  // A job whose closing event was missed while the tab was closed. Muted and
  // still, so it does not read as something that is happening now.
  interrupted: { icon: CircleSlash, className: 'text-text-muted' },
  partial: { icon: TriangleAlert, className: 'text-warning' },
  // Quiet for too long, or started without an answer: not a failure, a doubt.
  stalled: { icon: Clock, className: 'text-warning' },
  unconfirmed: { icon: CircleHelp, className: 'text-warning' },
};

const formatTime = (timestamp) =>
  new Date(timestamp).toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

const NotificationItem = ({ item, onDismiss }) => {
  const KindIcon = KIND_ICONS[item.kind] || Bell;
  const status = STATUS_STYLES[item.status] || STATUS_STYLES.running;
  const StatusIcon = status.icon;

  return (
    <div
      data-testid="notification-item"
      data-status={item.status}
      className={`rounded-xl border p-4 flex gap-3 transition-colors ${
        item.read
          ? 'border-border bg-transparent'
          : 'border-ninja-green/40 bg-ninja-green/5'
      }`}
    >
      <div className="w-9 h-9 rounded-lg bg-ninja-gray dark:bg-[#2d2d2d] flex items-center justify-center flex-shrink-0 text-text-secondary">
        <KindIcon size={16} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-sm text-ninja-dark dark:text-white truncate">
            {item.title}
          </p>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-xs text-text-muted">{formatTime(item.updatedAt)}</span>
            {onDismiss && (
              <button
                type="button"
                onClick={() => onDismiss(item.id)}
                aria-label={`Descartar ${item.title}`}
                title="Descartar"
                data-testid="notification-dismiss"
                className="w-6 h-6 rounded-md flex items-center justify-center text-text-muted hover:text-ninja-dark dark:hover:text-white hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] transition-colors"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-start gap-1.5 mt-1">
          <StatusIcon size={13} className={`${status.className} flex-shrink-0 mt-0.5`} />
          <p className="text-xs text-text-secondary">{item.message}</p>
        </div>

        {item.status === 'running' && item.progress?.total > 0 && (
          <div className="mt-2">
            <ProgressBar
              size="sm"
              value={item.progress.done}
              max={item.progress.total}
              label={`${item.progress.done} de ${item.progress.total}`}
              testId="notification-progress"
            />
          </div>
        )}

        {/* Only the deliveries export sends a link today: a presigned S3 URL
            that outlives this panel by seven days. The condition is on the
            payload, not on the kind, so another task that starts sending one
            gets the button for free. */}
        {item.url && (
          <Button
            variant="ghost"
            size="sm"
            icon={Download}
            className="mt-3"
            onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
          >
            Descargar archivo
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * @param {object} props
 * @param {(id: string) => void} [props.onDismiss] - Drops a single entry.
 * @param {() => void} props.onClear - Drops the finished ones.
 * @param {boolean} [props.isSocketMissing] - A job is running and the live
 *   connection that reports its end is down.
 */
export const DownloadsSidebar = ({
  isOpen,
  onClose,
  items = [],
  onClear,
  onDismiss,
  isSocketMissing = false,
}) => {
  if (!isOpen) return null;

  const sidebarContent = (
    <div className="fixed inset-0 z-[100] flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Sidebar Panel */}
      <div className="relative w-full max-w-md h-full bg-white dark:bg-[#1a1a1a] shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col border-l border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-ninja-green/10 flex items-center justify-center text-ninja-green">
              <Bell size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold text-ninja-dark dark:text-white">
                Centro de Actividad
              </h2>
              <p className="text-sm text-text-secondary">Descargas y notificaciones</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-text-secondary hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-3">
          {isSocketMissing && (
            <p
              data-testid="socket-offline-notice"
              className="text-xs text-text-secondary rounded-xl border border-border px-4 py-3"
            >
              Sin conexión en tiempo real: los avisos de tus procesos pueden llegar tarde. Siguen
              corriendo en el servidor.
            </p>
          )}
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center text-text-secondary opacity-70">
              <Download size={32} className="mb-3 opacity-50" />
              <p className="font-medium text-sm">No hay actividad reciente</p>
              <p className="text-xs mt-1 max-w-[240px]">
                Tus exportaciones y cargas masivas te avisarán aquí cuando terminen.
              </p>
            </div>
          ) : (
            items.map((item) => (
              <NotificationItem key={item.id} item={item} onDismiss={onDismiss} />
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="px-6 py-4 border-t border-border flex items-center justify-between">
            {/* Said plainly, because it is the kind of thing a user discovers
                by losing a link: this list now survives a reload, and the end
                of the session is where it stops. */}
            <p className="text-xs text-text-muted">Se borra al cerrar la sesión</p>
            <Button variant="ghost" size="sm" onClick={onClear}>
              Limpiar
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(sidebarContent, document.body);
};
